/**
 * Animación de «se recolocan solas» al reordenar una lista (técnica FLIP: First, Last, Invert,
 * Play).
 *
 * Cuando cambia el orden, React mueve los nodos del DOM de golpe. Para que se vea fluido:
 *
 *  1. `capture()` — JUSTO ANTES de cambiar el orden, se anota dónde se ve cada elemento.
 *  2. React pinta el orden nuevo y cada elemento salta a su sitio final.
 *  3. En el `useLayoutEffect` —antes de que el navegador pinte— se calcula cuánto se ha movido
 *     cada uno y se le aplica el movimiento CONTRARIO, así que en pantalla sigue donde estaba.
 *  4. Se quita esa compensación con una transición, y el elemento se desliza a su sitio.
 *
 * Se mide la posición VISUAL, transformación incluida: si llega otro cambio a mitad de una
 * animación, el elemento sale desde donde se ve en ese instante y no da un salto.
 *
 * También escala (`scale`), porque en la galería una miniatura puede pasar al hueco grande de
 * la portada y viceversa.
 */
import { useLayoutEffect, useRef } from 'react';

const DURATION_MS = 240;
const EASING = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

export function useFlip() {
  const nodes = useRef(new Map<string, HTMLElement>());
  const snapshot = useRef<Map<string, DOMRect> | null>(null);

  /** `ref` de cada elemento animable, con un identificador estable (el de la foto). */
  function register(id: string) {
    return (element: HTMLElement | null) => {
      if (element) nodes.current.set(id, element);
      else nodes.current.delete(id);
    };
  }

  /** Llamar justo antes del `setState` que cambia el orden. */
  function capture() {
    const rects = new Map<string, DOMRect>();
    nodes.current.forEach((element, id) => rects.set(id, element.getBoundingClientRect()));
    snapshot.current = rects;
  }

  // Sin dependencias a propósito: corre tras cada pintado, y no hace nada si no hubo `capture`.
  useLayoutEffect(() => {
    const first = snapshot.current;
    if (!first) return;
    snapshot.current = null;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    nodes.current.forEach((element, id) => {
      const before = first.get(id);
      if (!before) return;
      element.style.transition = 'none';
      element.style.transform = '';
      const after = element.getBoundingClientRect();
      if (after.width === 0 || after.height === 0) return;
      const dx = before.left - after.left;
      const dy = before.top - after.top;
      const sx = before.width / after.width;
      const sy = before.height / after.height;
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5 && Math.abs(sx - 1) < 0.01 && Math.abs(sy - 1) < 0.01) {
        return;
      }
      element.style.transformOrigin = '0 0';
      element.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
      // Forzar el cálculo para que el navegador registre el punto de partida antes de animar.
      void element.offsetWidth;
      element.style.transition = `transform ${DURATION_MS}ms ${EASING}`;
      element.style.transform = '';
    });
  });

  return { register, capture };
}
